import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL } from '../constants/api';
import { useScrollPrevention } from '../hooks/useScrollPrevention';
import { useModalBackHandler } from '../hooks/useModalBackHandler';

interface MediaFile {
  Rumuz: string;
  Ad: string;
  Tip: string;
  Boyut: number;
  isVideo?: boolean;
}

interface ImageViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: MediaFile[];
  currentIndex: number;
}

export default function ImageViewerModal({ isOpen, onClose, images, currentIndex }: ImageViewerModalProps) {
  // Scroll engelleyici hook kullan
  useScrollPrevention(isOpen);
  
  const [selectedIndex, setSelectedIndex] = useState(currentIndex);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [direction, setDirection] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [displayIndex, setDisplayIndex] = useState(currentIndex);
  
  // Video durumu için state
  const [videoProcessingStatus, setVideoProcessingStatus] = useState<{ [key: number]: boolean }>({});
  
  // Yüklenen görselleri takip et
  const preloadedImages = useRef<Set<string>>(new Set());
  
  // Touch events için state
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  
  // Thumbnail scroll container ref'i
  const thumbnailScrollRef = useRef<HTMLDivElement>(null);

  // Modal geri düğmesi handler
  const handleModalClose = useCallback(() => {
    onClose();
  }, [onClose]);
  
  useModalBackHandler({ 
    isOpen, 
    onClose: handleModalClose, 
    enabled: true 
  });

  // currentIndex prop'u değiştiğinde selectedIndex'i güncelle
  useEffect(() => {
    setSelectedIndex(currentIndex);
    setDisplayIndex(currentIndex);
  }, [currentIndex]);

  // Modal açıldığında currentIndex'i doğru ayarla ve ilk görseli ön yükle
  useEffect(() => {
    if (isOpen) {
      setSelectedIndex(currentIndex);
      setDisplayIndex(currentIndex);
      setIsFirstRender(true);
      
      // İlk medya dosyasını ön yükle
      if (images[currentIndex]) {
        const currentFile = images[currentIndex];
        const fileRumuz = currentFile.Rumuz;
        
        // Sadece görsel dosyaları için preload yap (video için gerekli değil)
        if (!currentFile.isVideo) {
          // Görsel dosyası için normal preload
          if (!preloadedImages.current.has(fileRumuz)) {
            setIsLoading(true);
            preloadImage(fileRumuz).finally(() => {
              setIsLoading(false);
            });
          }
        }
      }
    }
  }, [isOpen, currentIndex, images]);


  // Loading bittiğinde display index'i güncelle
  useEffect(() => {
    if (!isLoading) {
      setDisplayIndex(selectedIndex);
    }
  }, [isLoading, selectedIndex]);

  // Seçili thumbnail'e scroll
  useEffect(() => {
    if (isOpen && thumbnailScrollRef.current && images.length > 1) {
      // Modal animasyonu tamamlandıktan sonra scroll yap
      const scrollTimer = setTimeout(() => {
        const thumbnailWidth = 72; // w-16 = 64px + gap = 8px ≈ 72px
        const containerWidth = thumbnailScrollRef.current!.clientWidth;
        const scrollLeft = (displayIndex * thumbnailWidth) - (containerWidth / 2) + (thumbnailWidth / 2);
        
        thumbnailScrollRef.current!.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: 'smooth'
        });
      }, 400); // Modal animasyonu tamamlandıktan sonra
      
      return () => clearTimeout(scrollTimer);
    }
  }, [isOpen, displayIndex, images]);

  // Görseli ön yükle
  const preloadImage = (rumuz: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (preloadedImages.current.has(rumuz)) {
        resolve();
        return;
      }

      const img = new Image();
      img.onload = () => {
        preloadedImages.current.add(rumuz);
        resolve();
      };
      img.onerror = () => {
        reject(new Error(`Görsel yüklenemedi: ${rumuz}`));
      };
      img.src = `${API_BASE_URL}/files/download/${rumuz}`;
    });
  };


  // Video durumunu kontrol et
  const checkVideoStatus = async (videoRumuz: string, videoIndex: number, showToastMessage: boolean = true) => {
    try {
      const response = await fetch(`${API_BASE_URL}/files/stream/${videoRumuz}`);

      // Video hazırlanıyor durumu - content-type kontrolü
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        // JSON response - muhtemelen processing durumu
        const data = await response.json();
        
        if (data.status === 'processing') {
          const retryAfter = data.retryAfter || 5;
          
          setVideoProcessingStatus(prev => ({ ...prev, [videoIndex]: true }));

          // Belirtilen süre sonra tekrar dene (sessizce)
          setTimeout(() => {
            checkVideoStatus(videoRumuz, videoIndex, false);
          }, retryAfter * 1000);

          return false; // Video hazır değil
        }
      } else if (response.ok && contentType && contentType.includes('video')) {
        // Video hazır - content-type video
        setVideoProcessingStatus(prev => ({ ...prev, [videoIndex]: false }));
        return true; // Video hazır
      }
      
      // Hata durumu
      setVideoProcessingStatus(prev => ({ ...prev, [videoIndex]: false }));
      return false;
    } catch (error) {
      console.error('Video durum kontrolü hatası:', error);
      setVideoProcessingStatus(prev => ({ ...prev, [videoIndex]: false }));
      return false;
    }
  };

  const handlePrevious = async () => {
    const nextIndex = selectedIndex > 0 ? selectedIndex - 1 : images.length - 1;
    const nextFile = images[nextIndex];
    
    if (!nextFile) return;

    // Sadece görsel dosyaları için preload kontrol et
    const isAlreadyLoaded = nextFile.isVideo ? true : preloadedImages.current.has(nextFile.Rumuz);
    
    if (!isAlreadyLoaded) {
      setIsLoading(true);
    }
    
    try {
      if (!nextFile.isVideo) {
        // Sadece görsel için preload
        await preloadImage(nextFile.Rumuz);
      }
      
      setDirection(-1);
      setSelectedIndex(nextIndex);
      setIsFirstRender(false);
    } catch (error) {
      console.error('Medya yüklenirken hata:', error);
    } finally {
      if (!isAlreadyLoaded) {
        setIsLoading(false);
      }
    }
  };

  const handleNext = async () => {
    const nextIndex = selectedIndex < images.length - 1 ? selectedIndex + 1 : 0;
    const nextFile = images[nextIndex];
    
    if (!nextFile) return;

    // Sadece görsel dosyaları için preload kontrol et
    const isAlreadyLoaded = nextFile.isVideo ? true : preloadedImages.current.has(nextFile.Rumuz);
    
    if (!isAlreadyLoaded) {
      setIsLoading(true);
    }
    
    try {
      if (!nextFile.isVideo) {
        // Sadece görsel için preload
        await preloadImage(nextFile.Rumuz);
      }
      
      setDirection(1);
      setSelectedIndex(nextIndex);
      setIsFirstRender(false);
    } catch (error) {
      console.error('Medya yüklenirken hata:', error);
    } finally {
      if (!isAlreadyLoaded) {
        setIsLoading(false);
      }
    }
  };

  const handleThumbnailClick = async (index: number) => {
    const targetFile = images[index];
    
    if (!targetFile || index === selectedIndex) return;

    // Sadece görsel dosyaları için preload kontrol et
    const isAlreadyLoaded = targetFile.isVideo ? true : preloadedImages.current.has(targetFile.Rumuz);
    
    if (!isAlreadyLoaded) {
      setIsLoading(true);
    }
    
    try {
      if (!targetFile.isVideo) {
        // Sadece görsel için preload
        await preloadImage(targetFile.Rumuz);
      }
      
      const newDirection = index > selectedIndex ? 1 : -1;
      setDirection(newDirection);
      setSelectedIndex(index);
      setIsFirstRender(false);
    } catch (error) {
      console.error('Medya yüklenirken hata:', error);
    } finally {
      if (!isAlreadyLoaded) {
        setIsLoading(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isLoading) return; // Loading sırasında klavye girişlerini engelle
    
    if (e.key === 'ArrowLeft') handlePrevious();
    if (e.key === 'ArrowRight') handleNext();
    if (e.key === 'Escape') onClose();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Touch event handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    if (isLoading) return;
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isLoading) return;
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (isLoading || !touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && images.length > 1) {
      handleNext();
    }
    if (isRightSwipe && images.length > 1) {
      handlePrevious();
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  };

  if (images.length === 0) {
    return null;
  }

  const currentImage = images[displayIndex];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="fixed inset-0 bg-black/20 dark:bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999]"
          onClick={onClose}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="dialog"
          aria-modal="true"
          aria-label="Görsel Görüntüleyici"
        >
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="relative w-[90vw] max-w-7xl h-[90vh] max-h-full mx-4 flex flex-col rounded-lg bg-black bg-opacity-30 backdrop-blur-md dark:border-2 dark:border-white dark:border-opacity-20"
          >
            {/* Üst Bar */}
            <div className="flex justify-between items-center p-4 bg-black bg-opacity-20 backdrop-blur-sm rounded-t-lg">
              <div className="text-white flex-1 min-w-0 mr-4">
                <h3 className="text-lg font-medium truncate">{currentImage.Ad}</h3>
                <p className="text-sm text-gray-300">
                  {formatFileSize(currentImage.Boyut)} • {displayIndex + 1} / {images.length}
                </p>
              </div>
              
              <div className="flex items-center space-x-2">
                {/* İndir Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`${API_BASE_URL}/files/download/${currentImage.Rumuz}`, '_blank');
                  }}
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </button>

                {/* Kapat Button */}
                <button
                  onClick={onClose}
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Görsel Container */}
            <div 
              className="relative flex-1 flex items-center justify-center overflow-hidden" 
              onClick={(e) => e.stopPropagation()}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {/* Loading durumunda boyut koruma için placeholder */}
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center z-20">
                  <div className="flex flex-col items-center text-white">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white mb-4"></div>
                    <p className="text-sm">Görsel yükleniyor...</p>
                  </div>
                </div>
              )}

              {isFirstRender ? (
                currentImage.isVideo ? (
                  videoProcessingStatus[displayIndex] ? (
                    // Video işleniyorsa loading göster
                    <div className="flex flex-col items-center text-white">
                      <svg className="animate-spin h-12 w-12 text-white mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 004 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <p className="text-sm">Video hazırlanıyor...</p>
                    </div>
                  ) : (
                    // Video player
                    <video
                      src={`${API_BASE_URL}/files/stream/${currentImage.Rumuz}`}
                      poster={`${API_BASE_URL}/files/read/${currentImage.Rumuz}`}
                      controls
                      className="max-w-full max-h-full"
                      style={{ visibility: isLoading ? 'hidden' : 'visible' }}
                      onError={async () => {
                        await checkVideoStatus(currentImage.Rumuz, displayIndex, false);
                      }}
                    />
                  )
                ) : (
                  <img
                    src={`${API_BASE_URL}/files/download/${currentImage.Rumuz}`}
                    alt={currentImage.Ad}
                    className="max-w-full max-h-full object-contain"
                    style={{ visibility: isLoading ? 'hidden' : 'visible' }}
                    draggable={false}
                  />
                )
              ) : (
                <div className="relative w-full h-full flex items-center justify-center">
                  {currentImage.isVideo ? (
                    videoProcessingStatus[displayIndex] ? (
                      // Video işleniyorsa loading göster
                      <motion.div
                        key={displayIndex}
                        initial={{ x: direction > 0 ? 300 : -300, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="flex flex-col items-center text-white"
                        style={{ visibility: isLoading ? 'hidden' : 'visible' }}
                      >
                        <svg className="animate-spin h-12 w-12 text-white mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 004 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="text-sm">Video hazırlanıyor...</p>
                      </motion.div>
                    ) : (
                      // Video player
                      <motion.video
                        key={displayIndex}
                        initial={{ x: direction > 0 ? 300 : -300, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        src={`${API_BASE_URL}/files/stream/${currentImage.Rumuz}`}
                        poster={`${API_BASE_URL}/files/read/${currentImage.Rumuz}`}
                        controls
                        className="max-w-full max-h-full"
                        style={{ visibility: isLoading ? 'hidden' : 'visible' }}
                        onError={async () => {
                          await checkVideoStatus(currentImage.Rumuz, displayIndex, false);
                        }}
                      />
                    )
                  ) : (
                    <motion.img
                      key={displayIndex}
                      initial={{ x: direction > 0 ? 300 : -300, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      src={`${API_BASE_URL}/files/download/${currentImage.Rumuz}`}
                      alt={currentImage.Ad}
                      className="max-w-full max-h-full object-contain"
                      style={{ visibility: isLoading ? 'hidden' : 'visible' }}
                      draggable={false}
                    />
                  )}
                </div>
              )}

              {/* Navigation Buttons */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isLoading) handlePrevious();
                    }}
                    disabled={isLoading}
                    className={`absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-3 rounded-full transition-all z-10 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isLoading) handleNext();
                    }}
                    disabled={isLoading}
                    className={`absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-3 rounded-full transition-all z-10 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}
            </div>

            {/* Alt Bar - Küçük Görsel Listesi */}
            {images.length > 1 && (
              <div className="p-4 bg-black bg-opacity-20 backdrop-blur-sm rounded-b-lg">
                <div ref={thumbnailScrollRef} className="overflow-x-auto max-w-full">
                  <div className="flex space-x-2 px-0.5 min-w-min">
                    {images.map((image, index) => (
                      <button
                        key={index}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isLoading) handleThumbnailClick(index);
                        }}
                        disabled={isLoading}
                        className={`
                          flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all
                          ${index === displayIndex 
                            ? 'border-blue-500 opacity-100' 
                            : 'border-transparent opacity-60 hover:opacity-80'
                          }
                          ${isLoading ? 'cursor-not-allowed' : ''}
                        `}
                      >
                        {image.isVideo ? (
                          // Video thumbnail için /read yolunu kullan
                          <div className="relative w-full h-full">
                            <img
                              src={`${API_BASE_URL}/files/read/${image.Rumuz}`}
                              alt={image.Ad}
                              className="w-full h-full object-cover"
                            />
                            {/* Video play ikonu */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-6 h-6 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                                <svg className="w-3 h-3 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M8 5v14l11-7z"/>
                                </svg>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <img
                            src={`${API_BASE_URL}/files/read/${image.Rumuz}`}
                            alt={image.Ad}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
} 